import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  listCategories: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/categories${qs}`);
  },
};

/* Slide in from the right, vertically centered */
const slideIn = keyframes`
  from { transform: translate(100%, -50%); }
  to   { transform: translate(0, -50%); }
`;

const RibbonContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  z-index: 999;
  animation: ${slideIn} 0.8s ease-out both;
  will-change: transform;

  &::before {
    content: "";
    position: absolute;
    top: 0; bottom: 0; right: 100%;
    width: 12px;
  }

  @media (max-width: 768px) { top: 50%; }
  @media (max-width: 480px) { top: 60%; }
`;

const Ribbon = styled.div`
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: white;
  padding: 1rem 0.5rem;
  border-radius: 10px 0 0 10px;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  outline: none;

  &:hover {
    transform: translateX(-10px);
    box-shadow: -10px 0 25px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) { padding: 0.8rem 0.4rem; }
  @media (max-width: 480px) { padding: 0.6rem 0.3rem; }
`;

const RibbonContent = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
  padding: 0.5rem;

  @media (max-width: 768px) { font-size: 0.8rem; padding: 0.4rem; }
  @media (max-width: 480px) { font-size: 0.7rem; padding: 0.3rem; }
`;

const RibbonText = styled.div`
  font-size: 0.8rem;
  @media (max-width: 768px) { font-size: 0.7rem; }
  @media (max-width: 480px) { font-size: 0.6rem; }
`;

/* Floating categories panel */
const Panel = styled.div`
  position: absolute;
  top: 50%;
  right: calc(100% - 1px);
  transform: translate(0, -50%);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  border: 1px solid rgba(2, 6, 23, 0.06);
  padding: 0.75rem;
  min-width: 240px;
  max-width: 260px;
  z-index: 1000;
`;

const PanelTitle = styled.div`
  font-weight: 800; color: #0f172a; font-size: 0.95rem; padding: 0.25rem 0.4rem 0.5rem;
`;

const Chips = styled.div`
  display: grid; grid-template-columns: 1fr; gap: 0.5rem;
`;

const ChipLink = styled(Link)`
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.6rem 0.75rem; background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 12px; color: #0f172a; text-decoration: none; font-weight: 700;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: #eef2f7;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  }

  span.emoji { font-size: 1.1rem; display: grid; place-items: center; }
`;

/* Emoji mapping (same idea as gallery) */
const EMOJI_BY_SLUG = {
  estimating: "📐",
  scheduling: "🗓️",
  "safety-qa": "🦺",
  safety: "🦺",
  coordination: "🤝",
  "contracts-docs": "📑",
  contracts: "📑",
};

/* Utility hook to detect mobile/touch (coarse pointer OR narrow width) */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
      setIsMobile(coarse || window.innerWidth <= 768);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
};

const PromptRibbon = () => {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]); // {_id,name,slug,emoji}
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.listCategories({ page: 1, pageSize: 20, sort: "nameAsc" });
        const list = (res.data || []).map(c => ({
          _id: c._id,
          name: c.name,
          slug: c.slug,
          emoji: EMOJI_BY_SLUG[c.slug] || "✨",
        }));
        setCats(list);
      } catch {
        setCats([]); // silent fail; panel will still show "All Categories"
      }
    })();
  }, []);

  /* Close on Escape for accessibility (desktop only) */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goToAllCategories = () => navigate("/prompt-gallery?cat=all");

  const handleMouseEnter = () => { if (!isMobile) setOpen(true); };
  const handleMouseLeave = () => { if (!isMobile) setOpen(false); };
  const handleFocus = () => { if (!isMobile) setOpen(true); };
  const handleBlur = (e) => { if (!isMobile && !e.currentTarget.contains(e.relatedTarget)) setOpen(false); };
  const handleClick = () => { if (isMobile) goToAllCategories(); else setOpen(v => !v); };
  const handleTouchStart = () => { if (isMobile) goToAllCategories(); };

  return (
    <RibbonContainer
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={-1}
      onTouchStart={handleTouchStart}
    >
      <Ribbon
        onClick={handleClick}
        role="button"
        aria-haspopup={!isMobile}
        aria-expanded={!isMobile && open}
        aria-label="Open construction prompt categories"
      >
        <RibbonContent>
          <RibbonText>CONSTRUCTION PROMPT GALLERY</RibbonText>
        </RibbonContent>

        {/* Desktop/tablet only: show hover panel */}
        {!isMobile && open && (
          <Panel onClick={(e) => e.stopPropagation()}>
            <PanelTitle>Browse Categories</PanelTitle>
            <Chips>
              {cats.map((c) => (
                <ChipLink key={c._id} to={`/prompt-gallery?cat=${encodeURIComponent(c.slug)}`}>
                  <span className="emoji">{c.emoji}</span>
                  <span>{c.name}</span>
                </ChipLink>
              ))}
              <ChipLink to="/prompt-gallery?cat=all">
                <span className="emoji">🧭</span>
                <span>All Categories</span>
              </ChipLink>
            </Chips>
          </Panel>
        )}
      </Ribbon>
    </RibbonContainer>
  );
};

export default PromptRibbon;
